import React, { useRef, useEffect, useMemo, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import * as echarts from 'echarts';
import moment from 'moment';
import { SelectBox } from 'devextreme-react';
import TenantService from "../../../api/tenant.service"

const MRP_SOLUTIONS_TENANT_ID = '0fcc4e26-8d5c-409c-a60c-a4a87c20d416';

export default function SyncMechanicalCharts({ width, eventData = [], temperatureData = [] }) {
  // Added a ref for the temperature chart
  const tempChartRef = useRef(null);
  const chartRefs = [useRef(null), useRef(null), useRef(null)];
  const [grouping, setGrouping] = useState('Monthly');
  const tenant = TenantService.getTenant()
  const isFahrenheit = tenant === MRP_SOLUTIONS_TENANT_ID;
  const tempUnit = isFahrenheit ? '°F' : '°C';

  // Process temperature data based on the unit
  const displayTempData = useMemo(() => {
    const sorted = [...temperatureData].sort((a, b) => new Date(a.time) - new Date(b.time));
    if (!isFahrenheit) {
      return sorted;
    } else {
      const fSorted = sorted.map(item => ({
        ...item,
        // Assuming the value key in your temperatureData is 'value' or similar
        // Adjust 'value' to the actual key name in your temperatureData objects
        temperature: item.temperature !== undefined ? (item.temperature * 1.8 + 32).toFixed(2) : item.temperature.toFixed(2)
      }));
      return fSorted;
    }


    
  }, [temperatureData, isFahrenheit]);

  // Existing Binning Logic for aggregated charts
  const processedData = useMemo(() => {
    if (!eventData || eventData.length === 0) return [];
    const bins = {};

    eventData.forEach((item) => {
      const m = moment(item.time);
      let binKey;
      if (grouping === 'Hourly') binKey = m.startOf('hour').format('YYYY-MM-DD HH:00:00');
      else if (grouping === 'Weekly') binKey = m.startOf('isoWeek').format('YYYY-MM-DD');
      else if (grouping === 'Monthly') binKey = m.startOf('month').format('YYYY-MM-DD');
      else binKey = m.startOf('day').format('YYYY-MM-DD');

      if (!bins[binKey]) {
        bins[binKey] = {
          time: binKey,
          shot_count: 0,
          cycle_period_sum: 0,
          cycle_period_count: 0,
          shot_count_total: 0,
        };
      }

      bins[binKey].shot_count += Number(item.shot_count || 0);
      bins[binKey].shot_count_total = Math.max(bins[binKey].shot_count_total, Number(item.shot_count_total || 0));

      if (item.cycle_period > 0) {
        bins[binKey].cycle_period_sum += Number(item.cycle_period);
        bins[binKey].cycle_period_count += 1;
      }
    });

    return Object.values(bins)
      .map((b) => ({
        ...b,
        cycle_period: b.cycle_period_count > 0 ? (b.cycle_period_sum / b.cycle_period_count).toFixed(2) : 0,
      }))
      .sort((a, b) => new Date(a.time) - new Date(b.time));
  }, [eventData, grouping]);

  // Temperature Chart Option (Raw Data, no binning)
  const getTempOption = () => {
    let lastDate = '';
    return {
      title: { text: 'Temperature', left: 'center', textStyle: { fontSize: 14 }, top: 4 },
      toolbox: {
        right: 10,
        feature: {
          restore: { title: 'Reset' },
          saveAsImage: { title: 'Save' }
        }
      },
      grid: { left: 60, right: 30, top: 40, bottom: 75 },
      tooltip: { 
        trigger: 'axis',
        formatter: (params) => {
          // 1. Get the formatted time from the axis (e.g., "Jan 14 15:30")
          // We use params[0].axisValue to get the header
          const timeHeader = moment(params[0].axisValue).format('MMM DD HH:mm');
          
          let res = `<b>${timeHeader}</b><br/>`;
          
          params.forEach((item) => {
            // 2. Get the value (assuming key is 'temperature')
            // If your data key is different, update 'temperature' below
            const val = item.value.temperature;
            
            // 3. Return only the marker and the value (removing the series name)
            res += `${item.marker} <b>${val} ${tempUnit}</b>`;
          });
          
          return res;
        }
      },
      dataset: {
        source: displayTempData.sort((a, b) => new Date(a.time) - new Date(b.time)),
      },
      xAxis: {
        type: 'time',
        axisLabel: {
          hideOverlap: true,
          formatter: (value) => {
            const m = moment(value);
            const dateStr = m.format('MMM DD');
            const timeStr = m.format('HH:mm');
  
            // logic: If it's the start of a day (00:00) or the date changed since the last tick
            if (dateStr !== lastDate) {
              lastDate = dateStr;
              // When zoomed out, show 'Dec 12'. 
              // When zoomed in, show 'Dec 12 00:00' to mark the day break.
              return timeStr === '00:00' ? dateStr : `${dateStr}\n${timeStr}`;
            }
  
            // If the date hasn't changed, only show the time
            // This prevents repeating "Dec 12" over and over
            return timeStr;
          },
        },
        // Optional: splitNumber helps ECharts decide how many ticks to try and draw
        splitNumber: 6, 
      },
      yAxis: { type: 'value', name: `Temperature [${tempUnit}]`, scale: true },
      dataZoom: [
        { show: true, realtime: true, xAxisIndex: [0] },
        { type: 'inside', realtime: true, xAxisIndex: [0] },
      ],
      series: [{ 
        // name: 'Temperature',
        type: 'line',
        lineStyle: { width: 2, color: '#FEC002' },
        itemStyle: { color: '#FEC002' },
        showSymbol: false,
        sampling: 'lttb',
        // --- Added professional areaStyle below ---
        areaStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            {
              offset: 0,
              color: 'rgba(249, 221, 101, 0.4)' // Your yellow color with 40% opacity at top
            },
            {
              offset: 1,
              color: 'rgba(249, 221, 101, 0)'   // Fades to completely transparent at bottom
            }
          ])
        }
        // ------------------------------------------
      }],
    };
  };

  const getOption = (yField, yName, color) => {
    let ylabel = yName;
    let title
    let chartType = yField === 'cycle_period' ? 'line' : 'bar';
    if (yField === 'shot_count') {
      ylabel = 'Cycles';
      title =  'Cycles';
    }
    else if (yField === 'cycle_period') {
      ylabel = 'Cycle Time [s]';
      title =  'Cycle Time';
    } 

    return {
      title: { text: title, left: 'center', textStyle: { fontSize: 14 },top: 4 },
      grid: { left: 60, right: 30, top: 40, bottom: 75 },
      tooltip: { trigger: 'axis' },
      dataset: { source: processedData },
      xAxis: {
        type: 'category',
        axisLabel: {
          formatter: (val) => moment(val).format(grouping === 'Hourly' ? 'HH:mm' : 'MMM DD'),
        },
      },
      yAxis: { type: 'value', name: ylabel, scale: true },
      dataZoom: [
        { show: true, realtime: true, xAxisIndex: [0] },
        { type: 'inside', realtime: true, xAxisIndex: [0] },
      ],
      series: [{ 
        type: chartType, 
        color: color, 
        encode: { x: 'time', y: yField }, 
        smooth: true,
        label: {
          show: true,           // Always show the label
          position: 'top',      // Position it above the bar/point
          distance: 10,         // Distance from the bar
          color: '#333',        // Text color
          fontSize: 12,
          formatter: (params) => {
            // params.value is the data row; access the yField value specifically
            return params.value[yField]; 
          }
        }
      }],
    };
  };

  // Keep ALL Charts Connected (including the new Temp chart)
  useEffect(() => {
    const allRefs = [tempChartRef, ...chartRefs];
    const instances = allRefs.map((r) => r.current?.getEchartsInstance()).filter(Boolean);
    
    if (instances.length === 0) return;
    
    const GROUP_ID = 'mechanical_sync';
    instances.forEach((chart) => {
      chart.group = GROUP_ID;
    });
    
    echarts.connect(GROUP_ID);
    return () => echarts.disconnect(GROUP_ID);
  }, [processedData, displayTempData, chartRefs]); // Re-connect if data changes

  const chartsConfig = [
    // { field: 'shot_count_total', label: 'Total Shot Count', color: '#4CAF50' },
    { field: 'shot_count', label: 'Shot Count', color: '#f9dd65' },
    { field: 'cycle_period', label: 'Cycle Time [s]', color: '#f9dd65' }
  ];

  const chartContainerStyle = {
    height: '32%',
    background: '#fff',
    padding: '0px',
    borderRadius: '8px',
    border: '1px solid #ddd',
  };

  return (
    <>
      <div className="cycles-container">
        <SelectBox
          style={{ width: '200px' }}
          dataSource={['Hourly', 'Daily', 'Weekly', 'Monthly']}
          value={grouping}
          onValueChanged={(e) => setGrouping(e.value)}
          label="Grouping"
          stylingMode="outlined"
        />
      </div>
      <div style={{ width: width || '100%', display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <div style={chartContainerStyle}>
          <ReactECharts
            ref={tempChartRef}
            option={getTempOption()}
            style={{ height: '100%', width: '100%' }}
            notMerge={true}
          />
        </div>
        {chartsConfig.map((cfg, index) => (
          <div key={cfg.field} style={chartContainerStyle}>
            <ReactECharts
              ref={chartRefs[index]}
              option={getOption(cfg.field, cfg.label, cfg.color)}
              style={{ height: '100%', width: '100%' }}
              notMerge={true}
            />
          </div>
        ))}
      </div>
    </>
  );
}